import {
  Form,
  Image,
  Input,
  Modal,
  Typography,
  Upload,
  Collapse,
  Row
} from 'antd';
import { FC, memo, useEffect, useState, useCallback } from 'react';
import { LANGUAGES } from 'constants/languages';
import { ValidateErrorEntity } from 'rc-field-form/lib/interface';
import { useFileUpload } from 'hooks';
import { CONFIG_PAGES } from 'constants/configs';
import { generateUniqueString } from '@medicusunion/front-utils';
import { CollapseProps } from 'antd/lib';
import { upsertConfig } from 'services/api/config';

import { getFileUrl } from 'utils/file';
import { showErrorMessage } from 'utils/showErrorMessage';
import { getInputDir } from 'utils/helpers';

const { TextArea } = Input;
const { Text } = Typography;

const itemValues = {
  title: '',
  content: '',
  description: ''
};

type ValuesType = typeof itemValues;
type ItemType = {
  id: string;
  icon?: string;
  values?: Record<string, ValuesType>;
};
type BodyType = Record<string, ValuesType>;

const { Panel } = Collapse;

interface IProps {
  id?: string;
  onClose(): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  results: Array<Record<string, any> & { id: string }>;
  onSaved?: () => void;
}

const HowItWorksModal: FC<IProps> = ({ id, onClose, results, onSaved }) => {
  const [form] = Form.useForm();

  const [icon, setIcon] = useState('');
  const [errorKeys, setErrorKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const { upload } = useFileUpload();

  // Hydrate the form for edit-mode from the parent's results array.
  useEffect(() => {
    if (!id) return;
    const item = results.find(entry => entry.id === id) as ItemType | undefined;

    if (!item) return;
    setIcon(item.icon ?? '');
    form.setFieldsValue(item.values);
  }, [id, results, form]);

  const onFinish = useCallback(
    async (values: BodyType) => {
      setErrorKeys([]);
      const input = { icon, values };

      const nextData = id
        ? results.map(item => (item.id === id ? { ...item, ...input } : item))
        : [
            ...results,
            {
              id: generateUniqueString(),
              ...input
            }
          ];

      try {
        setLoading(true);
        await upsertConfig(CONFIG_PAGES.HOW_IT_WORKS, { data: nextData });
        onSaved?.();
        onClose();
      } catch (err) {
        showErrorMessage(err);
      } finally {
        setLoading(false);
      }
    },
    [icon, id, onClose, onSaved, results]
  );

  const onFinishError = (
    errorInfo: ValidateErrorEntity<
      {
        visible: boolean;
      } & BodyType
    >
  ) => {
    setErrorKeys([]);
    const errors = new Set(
      errorInfo.errorFields.map(item => `${item.name[0]}`)
    );

    setErrorKeys(Array.from(errors));
  };

  const items: CollapseProps['items'] = LANGUAGES.map(item => {
    const hasError = errorKeys.includes(item.locale);

    return {
      key: item.key,
      label: item.label,
      forceRender: true,
      children: (
        <Panel
          header={
            <Text
              style={{
                color: `var(--${hasError ? 'error-color' : 'text-color'})`
              }}
            >
              {item.label}
              {hasError ? ' (error)' : ''}
            </Text>
          }
          key={item.locale}
          forceRender
        >
          <Form.Item
            name={[item.locale, 'title']}
            label="Title"
            rules={[{ required: true }]}
          >
            <Input dir={getInputDir(item.locale)} />
          </Form.Item>
          <Form.Item
            name={[item.locale, 'description']}
            label="Description"
            rules={[{ required: true }]}
          >
            <TextArea dir={getInputDir(item.locale)} />
          </Form.Item>
        </Panel>
      )
    };
  });

  return (
    <Modal
      open
      title={id ? 'Edit' : 'Create'}
      onCancel={onClose}
      width={600}
      onOk={form.submit}
      confirmLoading={loading}
      destroyOnClose
      okText="Save"
    >
      <Form
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishError}
        layout="vertical"
        size="large"
      >
        <Row justify="center">
          <Form.Item>
            <Upload
              customRequest={e =>
                upload(e.file as File).then(val => setIcon(val || ''))
              }
              multiple={false}
              maxCount={1}
              showUploadList={false}
            >
              <Image
                src={getFileUrl(icon)}
                width={100}
                height={100}
                preview={false}
                style={{ borderRadius: '50%' }}
              />
            </Upload>
          </Form.Item>
        </Row>
        <Collapse
          items={items}
          accordion
          defaultActiveKey={LANGUAGES[0].locale}
        />
      </Form>
    </Modal>
  );
};

export default memo(HowItWorksModal);
